import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Loader2, Crown, Lock, Check, ArrowRight, Gamepad2, GraduationCap, Sparkles, Calculator, FileText, Award, Copy, X } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import Icon from "@/components/Icon";
import { catColor } from "@/lib/colors";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";

const PREMIUM_FEATURES = ["Advanced life simulations", "Deep-dive masterclasses", "AI coaching programs", "Financial calculators", "Career templates", "Certificates", "Unlimited AI coach"];

export default function PremiumHub() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [hub, setHub] = useState(null);
  const [busy, setBusy] = useState(false);
  const [calc, setCalc] = useState(null);
  const [tpl, setTpl] = useState(null);
  const [prog, setProg] = useState(null);
  const [cert, setCert] = useState(null);

  const isPrem = user?.is_premium;

  useEffect(() => { api.get("/premium/hub").then((r) => setHub(r.data)).catch(() => {}); }, []);

  const upgrade = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/payments/checkout", { lookup_key: "premium_monthly", origin_url: window.location.origin, user_id: user.user_id });
      window.location.href = data.checkout_url;
    } catch { setBusy(false); toast.error("Could not start checkout"); }
  };

  const openTemplate = async (id) => {
    if (!isPrem) return upgrade();
    try { const { data } = await api.get(`/premium/templates/${id}`); setTpl(data); } catch { toast.error("Locked"); }
  };
  const openProgram = async (id) => {
    if (!isPrem) return upgrade();
    try { const { data } = await api.get(`/premium/programs/${id}`); setProg(data); } catch { toast.error("Locked"); }
  };
  const startCoaching = (session) => { navigate("/app/coach", { state: { starter: session.prompt } }); };

  if (!hub) return <div className="grid place-items-center py-32"><Loader2 className="animate-spin text-indigo-400" size={32} /></div>;

  const LockBadge = () => (
    <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest font-bold px-2 py-0.5 rounded-full bg-yellow-500/15 text-yellow-300 border border-yellow-500/25"><Crown size={11} /> Premium</span>
  );

  return (
    <div>
      {/* Header */}
      <div className="flex items-center gap-3 mb-6">
        <div className="h-12 w-12 rounded-2xl bg-gradient-to-br from-yellow-400 to-amber-500 grid place-items-center shadow-xl shadow-yellow-500/30"><Crown size={26} className="text-white" /></div>
        <div>
          <h1 className="font-display font-extrabold text-3xl tracking-tight leading-none">Premium</h1>
          <span className="text-sm text-muted-foreground">Where LyfePilot goes 300x deeper.</span>
        </div>
      </div>

      {/* Upgrade hero OR active banner */}
      {isPrem ? (
        <div className="glass rounded-3xl p-5 mb-8 border border-yellow-500/30 flex items-center gap-3">
          <Crown size={22} className="text-yellow-400" />
          <div><h2 className="font-display font-bold">Premium is active 🎉</h2><p className="text-sm text-muted-foreground">Everything below is unlocked. Enjoy.</p></div>
        </div>
      ) : (
        <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} className="relative glass rounded-3xl p-7 mb-8 overflow-hidden border border-yellow-500/25">
          <div className="absolute -right-12 -top-12 h-48 w-48 rounded-full bg-yellow-500/15 blur-3xl" />
          <div className="relative">
            <span className="text-[11px] uppercase tracking-widest font-bold text-yellow-400">Unlock everything</span>
            <div className="flex items-end gap-2 mt-1 mb-4"><span className="font-display font-extrabold text-5xl tracking-tighter">$4.99</span><span className="text-muted-foreground mb-2">/month</span></div>
            <div className="grid sm:grid-cols-2 gap-x-6 gap-y-2 mb-6">
              {PREMIUM_FEATURES.map((f) => <div key={f} className="flex items-center gap-2 text-sm"><Check size={15} className="text-emerald-400 shrink-0" />{f}</div>)}
            </div>
            <Button data-testid="upgrade-btn" onClick={upgrade} disabled={busy} className="w-full sm:w-auto px-8 py-4 rounded-2xl bg-gradient-to-r from-yellow-400 to-amber-500 hover:from-yellow-300 hover:to-amber-400 text-black font-bold text-base transition-all hover:-translate-y-0.5 active:scale-95">
              {busy ? <Loader2 className="animate-spin" size={18} /> : <><Crown size={18} className="mr-2" /> Upgrade to Premium</>}
            </Button>
            <p className="text-[11px] text-muted-foreground mt-3">Secure Stripe checkout · Cancel anytime · Test card 4242 4242 4242 4242</p>
          </div>
        </motion.div>
      )}

      {/* Advanced Simulations */}
      <Section title="Advanced Simulations" icon={Gamepad2} sub="High-stakes, multi-ending life scenarios">
        <div className="grid sm:grid-cols-2 gap-4">
          {hub.simulations.map((s) => {
            const col = catColor(s.category_key === "career" ? "cyan" : "emerald");
            return (
              <button key={s.key} data-testid={`prem-sim-${s.key}`} onClick={() => isPrem ? navigate(`/simulation/${s.key}`) : upgrade()}
                className={`relative text-left rounded-3xl p-6 glass border ${col.border} overflow-hidden group`}>
                <div className={`absolute -right-6 -top-6 h-28 w-28 rounded-full bg-gradient-to-br ${col.grad} opacity-20 blur-2xl`} />
                <div className="flex items-center justify-between mb-2"><LockBadge />{!isPrem && <Lock size={16} className="text-yellow-400" />}</div>
                <h3 className="font-display font-extrabold text-xl mb-1">{s.title}</h3>
                <p className="text-muted-foreground text-sm line-clamp-2 mb-3">{s.description}</p>
                <span className={`inline-flex items-center gap-1.5 text-sm font-bold ${col.text}`}>{isPrem ? "Play now" : "Unlock"} <ArrowRight size={15} /></span>
              </button>
            );
          })}
        </div>
      </Section>

      {/* Masterclasses */}
      <Section title="Masterclasses" icon={GraduationCap} sub="Long-form deep dives with worksheets">
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {hub.masterclasses.map((m) => {
            const col = catColor(catColorFor(m.category_key));
            return (
              <button key={m.id} data-testid={`masterclass-${m.id}`} onClick={() => isPrem ? navigate(`/lesson/${m.id}`) : upgrade()}
                className={`text-left rounded-2xl p-5 glass border ${m.completed ? "border-emerald-500/30" : col.border}`}>
                <div className="flex items-center justify-between mb-3"><div className={`h-10 w-10 rounded-xl bg-gradient-to-br ${col.grad} grid place-items-center`}><Icon name={m.category_key === 'career' ? 'briefcase' : m.category_key === 'money' ? 'wallet' : 'graduation'} size={18} className="text-white" /></div>{!isPrem ? <Lock size={15} className="text-yellow-400" /> : m.completed && <Check size={16} className="text-emerald-400" />}</div>
                <h3 className="font-display font-bold text-sm leading-snug mb-1">{m.title}</h3>
                <p className="text-[11px] text-muted-foreground">{m.content?.minutes} min · +{m.xp} XP</p>
              </button>
            );
          })}
        </div>
      </Section>

      {/* Coaching Programs */}
      <Section title="AI Coaching Programs" icon={Sparkles} sub="Guided, multi-session coaching tracks">
        <div className="grid sm:grid-cols-2 gap-4">
          {hub.coaching_programs.map((p) => (
            <button key={p.id} data-testid={`program-${p.id}`} onClick={() => openProgram(p.id)} className="text-left rounded-2xl p-5 glass border border-violet-500/25">
              <div className="flex items-center justify-between mb-2"><div className="h-10 w-10 rounded-xl bg-gradient-to-br from-violet-500 to-indigo-600 grid place-items-center"><Icon name={p.icon} size={18} className="text-white" /></div>{!isPrem && <Lock size={15} className="text-yellow-400" />}</div>
              <h3 className="font-display font-bold text-base mb-1">{p.title}</h3>
              <p className="text-muted-foreground text-sm line-clamp-2">{p.desc}</p>
              <span className="text-[11px] text-violet-400 font-bold mt-2 inline-block">{p.sessions.length} sessions · {p.weeks} weeks</span>
            </button>
          ))}
        </div>
      </Section>

      {/* Calculators */}
      <Section title="Financial Calculators" icon={Calculator} sub="Real tools for real decisions">
        <div className="grid sm:grid-cols-3 gap-4">
          {hub.calculators.map((c) => (
            <button key={c.id} data-testid={`calc-${c.id}`} onClick={() => isPrem ? setCalc(c.id) : upgrade()} className="text-left rounded-2xl p-5 glass border border-emerald-500/25">
              <div className="flex items-center justify-between mb-2"><div className="h-10 w-10 rounded-xl bg-gradient-to-br from-emerald-500 to-teal-400 grid place-items-center"><Icon name={c.icon} size={18} className="text-white" /></div>{!isPrem && <Lock size={15} className="text-yellow-400" />}</div>
              <h3 className="font-display font-bold text-sm mb-1">{c.title}</h3>
              <p className="text-[11px] text-muted-foreground">{c.desc}</p>
            </button>
          ))}
        </div>
      </Section>

      {/* Templates */}
      <Section title="Career Templates" icon={FileText} sub="Copy-paste resume, cover letter & scripts">
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {hub.templates.map((t) => (
            <button key={t.id} data-testid={`template-${t.id}`} onClick={() => openTemplate(t.id)} className="text-left rounded-2xl p-5 glass border border-cyan-500/25">
              <div className="flex items-center justify-between mb-2"><div className="h-10 w-10 rounded-xl bg-gradient-to-br from-cyan-500 to-blue-500 grid place-items-center"><Icon name={t.icon} size={18} className="text-white" /></div>{!isPrem && <Lock size={15} className="text-yellow-400" />}</div>
              <h3 className="font-display font-bold text-sm mb-0.5">{t.title}</h3>
              <p className="text-[11px] text-muted-foreground">{t.category}</p>
            </button>
          ))}
        </div>
      </Section>

      {/* Certificates */}
      <Section title="Certificates" icon={Award} sub="Complete a world to earn its certificate">
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {hub.certificates.map((c) => {
            const col = catColor(c.color);
            return (
              <div key={c.key} data-testid={`cert-${c.key}`} className={`rounded-2xl p-4 glass border ${c.earned ? col.border : "border-white/5"} ${!c.earned ? "opacity-70" : ""}`}>
                <div className="flex items-center gap-3">
                  <div className={`h-11 w-11 rounded-xl bg-gradient-to-br ${col.grad} grid place-items-center ${!c.earned ? "grayscale opacity-60" : ""}`}><Icon name={c.icon} size={20} className="text-white" /></div>
                  <div className="flex-1 min-w-0"><h3 className="font-display font-bold text-sm">{c.name}</h3><span className="text-[11px] text-muted-foreground">{c.completed}/{c.total} lessons</span></div>
                </div>
                {c.earned ? (
                  <Button data-testid={`view-cert-${c.key}`} onClick={() => isPrem ? setCert(c) : upgrade()} className="w-full mt-3 h-9 rounded-xl bg-gradient-to-r from-yellow-400 to-amber-500 text-black font-bold text-xs">{isPrem ? "View Certificate" : "Unlock"}</Button>
                ) : (
                  <div className="mt-3 h-1.5 rounded-full bg-white/5 overflow-hidden"><div className={`h-full rounded-full bg-gradient-to-r ${col.grad}`} style={{ width: `${c.progress}%` }} /></div>
                )}
              </div>
            );
          })}
        </div>
      </Section>

      {/* Modals */}
      <CalcModal id={calc} onClose={() => setCalc(null)} />
      <TemplateModal tpl={tpl} onClose={() => setTpl(null)} />
      <ProgramModal prog={prog} onClose={() => setProg(null)} onStart={startCoaching} />
      <CertModal cert={cert} name={user?.name} onClose={() => setCert(null)} />
    </div>
  );
}

const catColorFor = (k) => ({ money: "emerald", career: "cyan", housing: "orange", relationships: "pink", productivity: "yellow", law: "amber" }[k] || "indigo");

const Section = ({ title, icon: I, sub, children }) => (
  <div className="mb-9">
    <div className="mb-4"><h2 className="font-display font-bold text-lg flex items-center gap-2"><I size={18} className="text-yellow-400" /> {title}</h2><p className="text-xs text-muted-foreground mt-0.5">{sub}</p></div>
    {children}
  </div>
);

function CalcModal({ id, onClose }) {
  return (
    <Dialog open={!!id} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="glass border-white/10 rounded-3xl max-w-md">
        <DialogHeader><DialogTitle className="font-display">Calculator</DialogTitle><DialogDescription>Enter your numbers to see the result instantly.</DialogDescription></DialogHeader>
        {id === "budget" && <BudgetCalc />}
        {id === "compound" && <CompoundCalc />}
        {id === "loan" && <LoanCalc />}
      </DialogContent>
    </Dialog>
  );
}

const Field = ({ label, value, onChange, prefix }) => (
  <div><Label className="text-xs uppercase tracking-wider text-muted-foreground font-bold">{label}</Label>
    <div className="relative mt-1">{prefix && <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground text-sm">{prefix}</span>}
      <Input type="number" value={value} onChange={(e) => onChange(e.target.value)} className={`rounded-2xl bg-white/5 border-white/10 h-11 ${prefix ? "pl-7" : ""}`} /></div></div>
);
const Result = ({ label, value, color = "text-emerald-400" }) => (
  <div className="flex justify-between items-center py-2 border-b border-white/5"><span className="text-sm text-muted-foreground">{label}</span><span className={`font-display font-bold text-lg ${color}`}>{value}</span></div>
);
const money = (n) => "$" + Math.round(n).toLocaleString();

function BudgetCalc() {
  const [inc, setInc] = useState(3000);
  const i = parseFloat(inc) || 0;
  return (<div className="space-y-3"><Field label="Monthly take-home" value={inc} onChange={setInc} prefix="$" />
    <div className="pt-2"><Result label="Needs (50%)" value={money(i * 0.5)} color="text-cyan-400" /><Result label="Wants (30%)" value={money(i * 0.3)} color="text-yellow-400" /><Result label="Savings (20%)" value={money(i * 0.2)} color="text-emerald-400" /></div></div>);
}
function CompoundCalc() {
  const [m, setM] = useState(300); const [yrs, setYrs] = useState(30); const [rate, setRate] = useState(8);
  const monthly = parseFloat(m) || 0, n = (parseInt(yrs) || 0) * 12, r = (parseFloat(rate) || 0) / 100 / 12;
  const fv = r ? monthly * ((Math.pow(1 + r, n) - 1) / r) : monthly * n;
  const contrib = monthly * n;
  return (<div className="space-y-3"><Field label="Monthly investment" value={m} onChange={setM} prefix="$" /><div className="grid grid-cols-2 gap-3"><Field label="Years" value={yrs} onChange={setYrs} /><Field label="Return %" value={rate} onChange={setRate} /></div>
    <div className="pt-2"><Result label="You invest" value={money(contrib)} color="text-muted-foreground" /><Result label="Growth" value={money(fv - contrib)} color="text-yellow-400" /><Result label="Final value" value={money(fv)} color="text-emerald-400" /></div></div>);
}
function LoanCalc() {
  const [amt, setAmt] = useState(15000); const [rate, setRate] = useState(7); const [yrs, setYrs] = useState(5);
  const p = parseFloat(amt) || 0, r = (parseFloat(rate) || 0) / 100 / 12, n = (parseInt(yrs) || 0) * 12;
  const pay = r ? (p * r) / (1 - Math.pow(1 + r, -n)) : p / n;
  const total = pay * n;
  return (<div className="space-y-3"><Field label="Loan amount" value={amt} onChange={setAmt} prefix="$" /><div className="grid grid-cols-2 gap-3"><Field label="Rate %" value={rate} onChange={setRate} /><Field label="Years" value={yrs} onChange={setYrs} /></div>
    <div className="pt-2"><Result label="Monthly payment" value={money(pay)} color="text-cyan-400" /><Result label="Total interest" value={money(total - p)} color="text-rose-400" /><Result label="Total paid" value={money(total)} color="text-yellow-400" /></div></div>);
}

function TemplateModal({ tpl, onClose }) {
  const copy = () => { navigator.clipboard?.writeText(tpl.body); toast.success("Copied to clipboard"); };
  return (
    <Dialog open={!!tpl} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="glass border-white/10 rounded-3xl max-w-lg">
        <DialogHeader><DialogTitle className="font-display">{tpl?.title}</DialogTitle><DialogDescription>{tpl?.category} template · edit and make it yours.</DialogDescription></DialogHeader>
        <pre className="whitespace-pre-wrap text-xs leading-relaxed bg-black/30 rounded-2xl p-4 max-h-80 overflow-y-auto no-scrollbar font-sans">{tpl?.body}</pre>
        <Button data-testid="copy-template-btn" onClick={copy} className="rounded-2xl bg-indigo-600 hover:bg-indigo-500 text-white font-semibold"><Copy size={16} className="mr-2" /> Copy Template</Button>
      </DialogContent>
    </Dialog>
  );
}

function ProgramModal({ prog, onClose, onStart }) {
  return (
    <Dialog open={!!prog} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="glass border-white/10 rounded-3xl max-w-md">
        <DialogHeader><DialogTitle className="font-display">{prog?.title}</DialogTitle><DialogDescription>{prog?.desc}</DialogDescription></DialogHeader>
        <div className="space-y-2">
          {prog?.sessions.map((s, i) => (
            <button key={i} data-testid={`session-start-${i}`} onClick={() => onStart(s)} className="w-full flex items-center gap-3 rounded-2xl p-3 bg-white/5 hover:bg-white/10 text-left transition-colors">
              <div className="h-8 w-8 rounded-full bg-violet-600/20 text-violet-300 grid place-items-center font-bold text-xs shrink-0">{i + 1}</div>
              <span className="flex-1 text-sm font-medium">{s.title}</span>
              <Sparkles size={15} className="text-violet-400" />
            </button>
          ))}
        </div>
        <p className="text-[11px] text-muted-foreground">Tap any session to start it live with your AI coach.</p>
      </DialogContent>
    </Dialog>
  );
}

function CertModal({ cert, name, onClose }) {
  return (
    <Dialog open={!!cert} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="glass border-white/10 rounded-3xl max-w-md">
        <DialogHeader><DialogTitle className="font-display sr-only">Certificate</DialogTitle><DialogDescription className="sr-only">Your certificate of completion.</DialogDescription></DialogHeader>
        <div className="rounded-2xl border-2 border-yellow-500/40 p-8 text-center bg-gradient-to-b from-yellow-500/5 to-transparent">
          <Award size={44} className="text-yellow-400 mx-auto mb-3" />
          <p className="text-[10px] uppercase tracking-[0.3em] text-muted-foreground font-bold">Certificate of Completion</p>
          <p className="text-sm text-muted-foreground mt-4">This certifies that</p>
          <p className="font-display font-extrabold text-2xl my-1">{name}</p>
          <p className="text-sm text-muted-foreground">has mastered the</p>
          <p className="font-display font-bold text-xl text-yellow-400 my-1">{cert?.name} World</p>
          <p className="text-[11px] text-muted-foreground mt-4">LyfePilot · {new Date().toLocaleDateString()}</p>
        </div>
      </DialogContent>
    </Dialog>
  );
}
